import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Plus, Edit2, Trash2, Shield, UserCheck, Eye, EyeOff, Users, Loader2 } from 'lucide-react';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'text-indigo-400', bg: 'bg-indigo-500/15 border-indigo-500/30' },
  support: { label: 'Support', color: 'text-emerald-400', bg: 'bg-emerald-500/15 border-emerald-500/30' },
};

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  inactive: { label: 'Inactive', color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
};

const EMPTY_FORM = { name: '', email: '', password: '', role: 'support', status: 'active' };

export default function AdminMembers() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [showPw, setShowPw] = useState(false);

  const fetchMembers = useCallback(async () => {
    if (!user?.is_main_admin && user?.member_role !== 'admin') return;
    try {
      const { data } = await axios.get(`${API}/admin/members`);
      setMembers(data.members || []);
    } catch { toast.error('Failed to load members'); }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchMembers(); }, [fetchMembers]);

  const openAdd = () => { setEditMember(null); setForm(EMPTY_FORM); setShowPw(false); setShowModal(true); };
  const openEdit = (m) => { setEditMember(m); setForm({ name: m.name, email: m.email, password: '', role: m.role, status: m.status }); setShowPw(false); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Name and email are required'); return; }
    if (!editMember && !form.password) { toast.error('Password is required'); return; }
    setSaving(true);
    try {
      if (editMember) {
        const payload = { name: form.name, email: form.email, role: form.role, status: form.status };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/admin/members/${editMember.id}`, payload);
        toast.success('Member updated');
      } else {
        await axios.post(`${API}/admin/members`, form);
        toast.success('Member created');
      }
      setShowModal(false);
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save member');
    }
    setSaving(false);
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Delete ${m.name}? This cannot be undone.`)) return;
    setDeleting(m.id);
    try {
      await axios.delete(`${API}/admin/members/${m.id}`);
      toast.success('Member deleted');
      fetchMembers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
    setDeleting(null);
  };

  const isMainAdmin = user?.is_main_admin;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-members-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Team Members</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage admin panel access for your team</p>
          </div>
          {isMainAdmin && (
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={openAdd} data-testid="add-member-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Member
            </Button>
          )}
        </div>

        {/* Role legend */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-card border border-border rounded-xl p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-500/20 flex items-center justify-center shrink-0 mt-0.5">
              <Shield className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Admin</p>
              <p className="text-xs text-muted-foreground mt-0.5">Full access to all sections. Cannot delete the main admin account.</p>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center shrink-0 mt-0.5">
              <UserCheck className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Support</p>
              <p className="text-xs text-muted-foreground mt-0.5">Access limited to: Dashboard, Merchants, Blogs, Node Health, Support Tickets.</p>
            </div>
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading members...
            </div>
          ) : members.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No team members yet.</p>
              {isMainAdmin && <p className="text-xs mt-1">Click "Add Member" to invite your first team member.</p>}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="members-table">
              <thead>
                <tr className="border-b border-border bg-muted/30 text-muted-foreground text-xs">
                  <th className="text-left py-3 px-4 font-medium">Name</th>
                  <th className="text-left py-3 px-4 font-medium">Email</th>
                  <th className="text-center py-3 px-4 font-medium">Role</th>
                  <th className="text-center py-3 px-4 font-medium">Status</th>
                  <th className="text-left py-3 px-4 font-medium">Created</th>
                  {isMainAdmin && <th className="text-right py-3 px-4 font-medium">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {members.map(m => {
                  const role = ROLE_CONFIG[m.role] || ROLE_CONFIG.support;
                  const status = STATUS_CONFIG[m.status] || STATUS_CONFIG.active;
                  return (
                    <tr key={m.id} className="border-b border-border/40 hover:bg-muted/10 transition-colors" data-testid={`member-row-${m.id}`}>
                      <td className="py-3 px-4 font-medium text-foreground">{m.name}</td>
                      <td className="py-3 px-4 text-muted-foreground text-xs">{m.email}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${role.bg} ${role.color}`}>{role.label}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${status.bg} ${status.color}`}>{status.label}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                      </td>
                      {isMainAdmin && (
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                              onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`}>
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-400/60 hover:text-red-400"
                              onClick={() => handleDelete(m)} disabled={!!deleting}
                              data-testid={`delete-member-${m.id}`}>
                              {deleting === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md" data-testid="member-modal">
          <DialogHeader>
            <DialogTitle>{editMember ? 'Edit Member' : 'Add New Member'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input placeholder="John Doe" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} required data-testid="member-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" placeholder="john@company.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} required data-testid="member-email-input" />
            </div>
            <div className="space-y-2">
              <Label>{editMember ? 'New Password (leave blank to keep)' : 'Password'}</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} placeholder={editMember ? 'Leave blank to keep current' : 'Min. 8 characters'}
                  value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  required={!editMember} className="pr-10" data-testid="member-password-input" />
                <button type="button" onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={v => setForm(p => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="member-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="support">Support</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                  <SelectTrigger data-testid="member-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {form.role === 'support' && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <p className="text-xs text-amber-400">
                  <strong>Support access:</strong> Dashboard, Merchants, Blogs, Node Health (limited), Support Tickets only.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={saving} data-testid="save-member-btn">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (editMember ? 'Save Changes' : 'Add Member')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
